<template>
    <div class="o-guide-step-bubble guide-step-bubble">
        <MapLabel
            v-if="isShowMapLabel"
            :selected-unit="activeGuideStepIndex"
            target="guide_step"
            :position-x="activeGuideStepPosition['position-x']"
            :position-y="activeGuideStepPosition['position-y']"
            :offset-x="offsetX"
            :offset-y="offsetY"
            ref="mapLabel"
        >
            <ADialog
                :class="[
                    'step-direction__dialog',
                    `step-direction--${activeGuideStepPosition.class}`
                ]"
                ref="aDialog"
            >
                <template>
                    <span
                        class="guide-steps__step-number"
                        v-if="isMobile"
                    >
                        {{ activeGuideStepIndex+1 }}
                    </span>
                    <AMapIcon v-if="shouldShowStepIcon" :direction ="activeGuideStep.direction"/>
                    <OStepText
                        v-if="isShowMapStep"
                        class="d-path-steps__step"
                        :step="activeGuideStep"
                    />
                </template>
            </ADialog>
        </MapLabel>
        <div
            class="o-guide-step-bubble-point-wrapper"
            v-if="isVerticesShow"
        >
            <div
                v-for="(item, index) of verticesToShow"
                :key="index"
                :style="getPointStyle(item)"
                :class="['o-guide-step-bubble-point-wrapper__point', `point-${index}`]"
            >
                {{index}}
            </div>
        </div>
    </div>
</template>

<script>
import ADialog from "../../atoms/a-dialog/a-dialog";
import AMapIcon from "../../atoms/a-map-icon/a-map-icon";
import OStepText from "../../unique-organisms/o-step-text/o-step-text";
import MapLabel from "../../../shared/components/map-viewer/labels/map-label";

import { getScale } from '@/utils/helpers/fit-to-screen';

import { UI_ON_CAMERA_UPDATE } from "../../../shared/components/map-viewer/constants/map-events";

import debounce from 'lodash/debounce';
import uniqWith from 'lodash/uniqWith';
import isEqual from 'lodash/isEqual';

import {
    currentOrientation,
    getStepTopScreenPos,
    getPointOnFloorScreenPos
} from '../../../utils/services'

import {
    mapState,
    mapGetters
} from 'vuex'

export default {
    name: 'OGuideStepBubble',
    inject: ['mapViewer'],
    components: {
        ADialog,
        AMapIcon,
        MapLabel,
        OStepText
    },
    props: {
        guideSteps: {
            type: Array,
            required: true
        },
        activeGuideStepIndex: {
            type: Number,
            required: true
        },
        shouldShowGuideStepText: {
            type: Boolean,
            default: false
        },
        vertices: {
            type: Array,
            default: () => []
        }
    },
    data: () => ({
        currentOrientation: 0,
        activeGuideStepPosition: {
            'position-x': 'left',
            'position-y': 'bottom',
            'class': 'left'
        },
        canCheckBubblesPosition: true,
        offsetX: -21,
        offsetY: 15,
        updateOrientationTimeout: null,
        descriptionStepPosition : {
            leftTop: {
                'position-x': 'left',
                'position-y': 'bottom',
                'class': 'left-top'
            },
            rightTop: {
                'position-x': 'right',
                'position-y': 'bottom',
                'class': 'right-top'
            },
            middleTop: {
                'position-x': 'middle',
                'position-y': 'bottom',
                'class': 'middle-top'
            },
            leftBottom: {
                'position-x': 'left',
                'position-y': 'top',
                'class': 'left-bottom'
            },
            rightBottom: {
                'position-x': 'right',
                'position-y': 'top',
                'class': 'right-bottom'
            },
            middleBottom: {
                'position-x': 'middle',
                'position-y': 'top',
                'class': 'middle-bottom'
            }
        },
        verticesToShow: [],
        isVerticesShow: false
    }),
    computed: {
        ...mapState({
            distance: state => state.activePath?.distance,
            isLandscape: state => state.landscape,
            locale: state => state.activeLocale
        }),
        ...mapGetters([
            'getMapConfigProp'
        ]),
        activeGuideStep(){
            return this.guideSteps[this.activeGuideStepIndex]
        },
        isMobile(){
            return document.location.href.includes('mobile')
        },
        isDistanceEnough() {
            return this.guideSteps.length < 3 ? this.distance > this.getMapConfigProp('distance_befor_hide_steps', 100) : true
        },
        isShowMapLabel() {
            return this.activeGuideStepIndex !== this.guideSteps.length -1 && this.isDistanceEnough
        },
        isShowMapStep() {
            return (this.shouldShowGuideStepText || true) && !this.isMobile
        },
        shouldShowStepIcon() {
            return this.getMapConfigProp('guide_camera.steps.active.show_icon', true);
        }
    },
    methods: {
        initEventListenersBubblesPosition() {
            this.$bus.$on(UI_ON_CAMERA_UPDATE, this.updateOrientation);
        },
        deactivateEventListenersBubblesPosition() {
            this.$bus.$off(UI_ON_CAMERA_UPDATE, this.updateOrientation);
        },
        checkBubblesPosition() {
            // if distance from start to finish not enough we don't show bubbles and we don't need this calculation
            // and if (this.activeGuideStepIndex === this.guideSteps.length -1) we don't show bubbles and we don't need this calculation
            if(!this.isDistanceEnough || this.activeGuideStepIndex === this.guideSteps.length -1) return

            let position = null

            if(this.checkVertexInclude() || this.checkBubblesPositionRelativeScreen())
                position = this.changePosition()

            if (position && this.activeGuideStepPosition !== position) this.activeGuideStepPosition = position
        },
        changeOffset() {
            const bubblePosition = this.$refs.mapLabel && this.$refs.mapLabel._vnode.elm.getBoundingClientRect()

            const arrowPosition = this.$refs.mapLabel._vnode.elm.querySelector('.dialog__arow').getBoundingClientRect()

            if( // change offsetX
                this.activeGuideStepPosition === this.descriptionStepPosition.middleBottom
                || this.activeGuideStepPosition === this.descriptionStepPosition.middleTop
            ) {
                this.offsetX = 0
            } else if (
                this.activeGuideStepPosition === this.descriptionStepPosition.rightTop
                || this.activeGuideStepPosition === this.descriptionStepPosition.rightBottom
            ) {
                this.offsetX = arrowPosition.right - bubblePosition.right
            } else {
                this.offsetX = bubblePosition.left - arrowPosition.left
            }

            if( // change offsetY
                this.activeGuideStepPosition === this.descriptionStepPosition.middleBottom
                || this.activeGuideStepPosition === this.descriptionStepPosition.leftBottom
                || this.activeGuideStepPosition === this.descriptionStepPosition.rightBottom
            ) {
                this.offsetY = bubblePosition.top - arrowPosition.top
            } else {
                this.offsetY = arrowPosition.bottom - bubblePosition.bottom
            }
        },
        /**
         * this function checks if the bubble does not cross the border of the map, if it crosses we need to change the position of the bubble
         * @param bubble object - the coordinates of the bubble we want to check
         * return Boolean , true if bubble cross the border of the map
         */
        checkBubblesPositionRelativeScreen(bubble) {
            const bubblePosition = bubble || (this.$refs.mapLabel && this.$refs.mapLabel._vnode.elm.getBoundingClientRect());

            if(!bubblePosition) return;

            const headerHeight = this.isMobile ?
                this.isLandscape ? 0 :document.querySelector('.m-header').clientHeight
                : document.querySelector('.header').clientHeight
            const mapParameters = document.querySelector('.mapviewer__iframe').getBoundingClientRect()
            const footerHieght = document.querySelector('.o-mobile-guide-steps')
                ? document.querySelector('.o-mobile-guide-steps').clientHeight
                : 0

            const topBubblesPosition = bubblePosition.y - headerHeight
            const bottomBubblesPosition = document.documentElement.clientHeight - bubblePosition.y - bubblePosition.height - footerHieght
            const rigthBubblesPosition  = document.documentElement.clientWidth - bubblePosition.x - bubblePosition.width
            const leftBubblesPosition = bubblePosition.x - mapParameters.x

            if(topBubblesPosition <= 0 || rigthBubblesPosition <= 0 || leftBubblesPosition <= 0 || bottomBubblesPosition <= 0) {
                return true
            } else {
                return false
            }

        },
        /**
         *  this function checking vertex inside bubbles
         *  @param bubble object - the coordinates of the bubble we want to check
         *  @param isStrictVertices boolean - if it is impossible to place the bubble so that it does not cross any vertex, priority is given to the vertices of the active step
         */
        checkVertexInclude(bubble = null, isStrictVertices = false) {
            const verticesCordinate = isStrictVertices ? this.getVerticesCordinate(true) : this.getVerticesCordinate()

            const bubblePosition = bubble || (this.$refs.mapLabel && this.$refs.mapLabel._vnode.elm.getBoundingClientRect())

            if(!bubblePosition || !verticesCordinate) return;

            const mapParameters = document.querySelector('.mapviewer__iframe').getBoundingClientRect()

            const xBubblesCordinate = [bubblePosition.x - mapParameters.x, bubblePosition.x + bubblePosition.width - mapParameters.x]
                .sort((a,b) => a > b ? 1 : -1)
            const yBubblesCordinate = [bubblePosition.y - mapParameters.y, bubblePosition.y + bubblePosition.height - mapParameters.y]
                .sort((a,b) => a > b ? 1 : -1)

            let isVertexInclude = false

            let clientWidthOffset = 30;
            let clientHeightOffset = 20;

            if(!this.$route.query.terminalMode) {
                const scale = getScale();
                clientHeightOffset = clientHeightOffset*scale;
                clientWidthOffset = clientWidthOffset*scale;
            }

            verticesCordinate.forEach((vertex, index, arr) => {
                let xInclude = false
                let yInclude = false

                if(vertex.x >= xBubblesCordinate[0] - clientWidthOffset && vertex.x <= xBubblesCordinate[1] + clientWidthOffset) {
                    xInclude = true
                }
                if(vertex.y >= yBubblesCordinate[0] - clientHeightOffset && vertex.y <= yBubblesCordinate[1] + clientHeightOffset) {
                    yInclude = true
                }
                if( xInclude && yInclude) {
                    isVertexInclude = true
                }
            })
            return isVertexInclude
        },
        /**
         *  this function return vertices which are closest to the active step so we can check if the bubble crosses the vertex
         *  @param isStrictVertices boolean - if it is impossible to place the bubble so that it does not cross any vertex, priority is given to the vertices of the active step,
         * then  vertices previous step and next step ignore
         * return Array
         */
        getVerticesCordinate(isStrictVertices = false) {
            const verticesCordinate = []

            const prevStepVerticesId =  this.activeGuideStepIndex !== 0 ? this.guideSteps[this.activeGuideStepIndex - 1].vertex_id : null // this is previous step vertexId
            const currentStepVerticesId = this.guideSteps[this.activeGuideStepIndex].vertex_id // this is current step vertexId
            const nextStepVerticesId = this.activeGuideStepIndex !== this.guideSteps.length - 1 ? this.guideSteps[this.activeGuideStepIndex + 1].vertex_id : null  // this is next step vertexId
            const afterNextStepVerticesId = this.activeGuideStepIndex !== this.guideSteps.length - 2 ? this.guideSteps[this.activeGuideStepIndex + 2].vertex_id : null  // this is after next step vertexId

            const prevStepVerticesIndex = prevStepVerticesId !== null ? this.vertices.findIndex(vert => vert.id === prevStepVerticesId) : null
            const currentStepVerticesIndex = this.vertices.findIndex(vert => vert.id === currentStepVerticesId) // we have all guide vertices(in props),  we need current vertex index of this all vertices
            const nextStepStepVerticesIndex = nextStepVerticesId !== null ? this.vertices.findIndex(vert => vert.id === nextStepVerticesId) : null
            const afterNextStepStepVerticesIndex = afterNextStepVerticesId !== null ? this.vertices.findIndex(vert => vert.id === afterNextStepVerticesId) : null

            const prevStepVertices = prevStepVerticesIndex !== null ?  this.vertices.slice(prevStepVerticesIndex, currentStepVerticesIndex + 1) : []
            const currentStepVertices = nextStepStepVerticesIndex !== null ?  this.vertices.slice(currentStepVerticesIndex, nextStepStepVerticesIndex + 1) : []
            const nextStepStepVertices = nextStepStepVerticesIndex !== null && afterNextStepStepVerticesIndex !== null ?  this.vertices.slice(nextStepStepVerticesIndex, afterNextStepStepVerticesIndex + 1) : []

            let allStepVertices = []

            if (isStrictVertices)  allStepVertices = [...currentStepVertices]
            else allStepVertices = [...prevStepVertices, ...currentStepVertices, ...nextStepStepVertices]

            allStepVertices.forEach( vertex => {
                const floor = this.mapViewer.mapData.objects.find( o => o.id === vertex.floor )
                const vertexCordinate = { ...getPointOnFloorScreenPos(vertex.position, floor.unit.name)}

                verticesCordinate.push(vertexCordinate) // verticesCordinate.push({...vertexCordinate, z : vertex.position.z})
            })

            let allVertices = this.addMoreVertices(verticesCordinate)
            this.verticesToShow = allVertices

            if(!this.$route.query.terminalMode) {
                const scale = getScale();
                allVertices = allVertices.map(({x, y, z}) => ({x: x*scale, y: y*scale, z}))
            }

            return allVertices
        },
        /**
         *   --------- MAIN FUNCTION --------
         *  this function returns the new location of the bubble
         *  return Object  - new bubbles location
         */
        changePosition() {
            const bubblePosition = this.$refs.mapLabel && this.$refs.mapLabel._vnode.elm.getBoundingClientRect()

            const mapParameters = document.querySelector('.mapviewer__iframe').getBoundingClientRect();
            let topStepNumberPosition = getStepTopScreenPos(this.activeGuideStepIndex);
            let clientOffsetHeight = 80;

            if(!this.$route.query.terminalMode) {
                const scale = getScale();
                topStepNumberPosition = {x: topStepNumberPosition.x*scale, y: topStepNumberPosition.y*scale, z: topStepNumberPosition.z*scale}
                clientOffsetHeight = clientOffsetHeight*scale;
            }

            const topMiddlePosition = {
                x: topStepNumberPosition.x - bubblePosition.width/2 + mapParameters.x,
                y: topStepNumberPosition.y - bubblePosition.height - this.offsetY + mapParameters.y,
                height: bubblePosition.height,
                width: bubblePosition.width
            }

            const topRightPosition = {
                x: topStepNumberPosition.x - bubblePosition.width - this.offsetX + mapParameters.x,
                y: topStepNumberPosition.y - bubblePosition.height - this.offsetY + mapParameters.y,
                height: bubblePosition.height,
                width: bubblePosition.width
            }

            const topLeftPosition = {
                x: topStepNumberPosition.x + this.offsetX+ mapParameters.x,
                y: topStepNumberPosition.y - bubblePosition.height - this.offsetY + mapParameters.y,
                height: bubblePosition.height,
                width: bubblePosition.width
            }

            const bottomMiddlePosition = {
                x: topStepNumberPosition.x - bubblePosition.width/2 + mapParameters.x,
                y: topStepNumberPosition.y + clientOffsetHeight + mapParameters.y,
                height: bubblePosition.height,
                width: bubblePosition.width
            }

            const bottomLeftPosition = {
                x: topStepNumberPosition.x - bubblePosition.width - this.offsetX + mapParameters.x,
                y: topStepNumberPosition.y + clientOffsetHeight + mapParameters.y,
                height: bubblePosition.height,
                width: bubblePosition.width
            }

            const bottomRightPosition = {
                x: topStepNumberPosition.x - bubblePosition.width - this.offsetX + mapParameters.x,
                y: topStepNumberPosition.y + clientOffsetHeight + mapParameters.y,
                height: bubblePosition.height,
                width: bubblePosition.width
            }

            if(this.checkBubblesPositionFunctions(topMiddlePosition)) {
                return this.descriptionStepPosition.middleTop
            } else if(this.checkBubblesPositionFunctions(topRightPosition)) {
                return this.descriptionStepPosition.rightTop
            } else if(this.checkBubblesPositionFunctions(topLeftPosition)) {
                return this.descriptionStepPosition.leftTop
            } else if(this.checkBubblesPositionFunctions(bottomRightPosition)) {
                return this.descriptionStepPosition.rightBottom
            } else if(this.checkBubblesPositionFunctions(bottomLeftPosition)) {
                return this.descriptionStepPosition.leftBottom
            } else if(this.checkBubblesPositionFunctions(bottomMiddlePosition)) {
                return this.descriptionStepPosition.middleBottom
            } else if(this.checkBubblesPositionFunctions(topMiddlePosition, true)) {
                return this.descriptionStepPosition.middleTop
            } else if(this.checkBubblesPositionFunctions(topRightPosition, true)) {
                return this.descriptionStepPosition.rightTop
            } else if(this.checkBubblesPositionFunctions(topLeftPosition, true)) {
                return this.descriptionStepPosition.leftTop
            } else if(this.checkBubblesPositionFunctions(bottomRightPosition, true)) {
                return this.descriptionStepPosition.rightBottom
            } else if(this.checkBubblesPositionFunctions(bottomLeftPosition, true)) {
                return this.descriptionStepPosition.leftBottom
            } else if(this.checkBubblesPositionFunctions(bottomMiddlePosition, true)) {
                return this.descriptionStepPosition.middleBottom
            } else {
                //return this.descriptionStepPosition.middleBottom
                return this.activeGuideStepPosition
            }
        },
        /**
         * if the distance between the vertices is too large, additional vertices must be added
         * @param vetrices - Array required
         */
        addMoreVertices(vertices) {
            let verticesCordinate = vertices

            const distanceBeetwenVertices = this.getMapConfigProp('distanceBeetwenVertices', 50)

            verticesCordinate.forEach((vertex, index, arr) => {
                let vertices = []
                let xCordinate = []
                let yCordinate = []

                let numberNewPoint = 0
                const newCordinate = []

                if(index !== arr.length -1) {
                    vertices = [vertex, arr[index + 1]]
                } else {
                    return
                }
                xCordinate = [vertices[0].x, vertices[1].x]
                yCordinate = [vertices[0].y, vertices[1].y]

                if(xCordinate[1] - xCordinate[0] > distanceBeetwenVertices || yCordinate[1] - yCordinate[0] > distanceBeetwenVertices || xCordinate[1] - xCordinate[0] < -distanceBeetwenVertices || yCordinate[1] - yCordinate[0] < -distanceBeetwenVertices) {
                    numberNewPoint = Math.abs(xCordinate[1] - xCordinate[0]) > Math.abs(yCordinate[1] - yCordinate[0])
                        ? Math.abs(Math.floor((xCordinate[1] - xCordinate[0])/distanceBeetwenVertices))
                        : Math.abs(Math.floor((yCordinate[1] - yCordinate[0])/distanceBeetwenVertices))

                    for(let i = 0; i < numberNewPoint; i++) {
                        newCordinate.push({
                            x: (xCordinate[0] + (newCordinate[i - 1] ? newCordinate[i - 1].x  : xCordinate[1]))/2,
                            y: (yCordinate[0] + (newCordinate[i - 1] ? newCordinate[i - 1].y  : yCordinate[1]))/2,
                            // z: vertex.z,
                            customPoint: true // we use this line during development can be removed
                        })

                        newCordinate.push({
                            x: (xCordinate[1] + (newCordinate[i - 1] ? newCordinate[i - 1].x  : xCordinate[0]))/2,
                            y: (yCordinate[1] + (newCordinate[i - 1] ? newCordinate[i - 1].y  : yCordinate[0]))/2,
                            // z: vertex.z,
                            customPoint: true // we use this line during development can be removed
                        })
                    }
                    verticesCordinate = [...verticesCordinate, ...newCordinate]
                } else {
                    return
                }
            })

            return uniqWith(verticesCordinate, isEqual)
        },
        /**
         * helper
         * return boolean
         */
        checkBubblesPositionFunctions(bubble, isStrictVertices = false) {
            return (
                !this.checkVertexInclude(bubble, isStrictVertices)
                && !this.checkBubblesPositionRelativeScreen(bubble)
            )
        },
        /**
         * this function return point style: bg-color, top, left ant other
         * @param {object} point required
         * return {string}
         */
        getPointStyle(point) {
            return `
                background-color: ${point.customPoint ? '#1cff00' : '#3700ff'};
                top: ${point.y}px;
                left: ${point.x}px`
        }
    },
    watch: {
        activeGuideStepIndex () {
            this.$nextTick(() => this.checkBubblesPosition())
        },
        isLandscape() {
            setTimeout(() => {
                // this.activeGuideStepPosition = {}
                // this.checkBubblesPosition()
                this.$bus.$emit(UI_ON_CAMERA_UPDATE)
            }, 100)
        },
        locale() {
            this.$nextTick(() => {
                this.activeGuideStepPosition = {}
                this.checkBubblesPosition()
            })
        },
        activeGuideStepPosition() {
            this.$nextTick(() => this.changeOffset())
        }
    },
    mounted() {
        this.initEventListenersBubblesPosition()
        this.isVerticesShow = this.getMapConfigProp('is_vertices_show', false)
    },
    created() {
        this.updateOrientation = debounce(() => {
            this.currentOrientation = currentOrientation();
            this.checkBubblesPosition();
        }, 300)
    },
    beforeDestroy() {
        this.deactivateEventListenersBubblesPosition()
        clearTimeout(this.updateOrientationTimeout)
    },
}
</script>

<style lang="scss">
@import "o-guide-step-bubble";
</style>
